import { useSuspenseQuery } from '@tanstack/react-query';
import { parseFullName } from 'parse-full-name';
import * as React from 'react';

import type * as api from '@gitbook/api-client';

import { apiQuery, useCurrentUser } from '../../state';
import { capitalizeFirstLetter } from '../../utils';

/**
 * Pretty name for an user, using their ID.
 */
export function UserPrettyName(props: { userId: string; you?: boolean }): React.ReactElement {
    const { userId, you = true } = props;
    const { data: user } = useSuspenseQuery(
        apiQuery('getUserById', { userId }, { nullOnError: true })
    );

    return <UserProfilePrettyName user={user || null} you={you} />;
}

/**
 * Pretty name for a user.
 */
export function UserProfilePrettyName(props: {
    user: Pick<api.User, 'id' | 'displayName'> | null;
    you?: boolean;
    fallback?: string;
    capitalize?: boolean;
}): React.ReactElement {
    const { user, you = true, fallback = 'someone', capitalize = false } = props;
    const currentUser = useCurrentUser();
    const name = (() => {
        if (user) {
            if (you && user.id === currentUser?.uid) {
                return 'you';
            }
            return formatUserPrettyName(user.displayName);
        }
        return fallback;
    })();

    return <>{capitalize ? capitalizeFirstLetter(name) : name}</>;
}

/**
 * List of user names.
 */
export function UserPrettyNames(props: { userIds: string[]; you?: boolean }): React.ReactElement {
    const { userIds, you = true } = props;

    return (
        <>
            {userIds.map((userId, index) => (
                <React.Fragment key={userId}>
                    {index > 0 ? ', ' : null}
                    <UserPrettyName userId={userId} you={you} />
                </React.Fragment>
            ))}
        </>
    );
}

/*
 * Format an user name into a short pretty name.
 */
export function formatUserPrettyName(name: string): string {
    const parsed = parseFullName(name);
    return parsed.first ? parsed.first : name;
}
